/**
 * Account / order / fill / position / risk state from the trading socket (SPEC §7.1–§7.3).
 *
 * `reduceTrading` is a pure reducer over `Envelope`s. It must stay correct under the replay-ring
 * semantics of `stream/client.ts`: after a reconnect the gateway replays ordered frames after
 * `last_seq` (and, on a first connect, from the start of the ring), so the same frame can arrive
 * twice and a replayed older frame can arrive after a live newer one. Therefore:
 *
 * - order, position, account and risk frames are full state, keyed, and applied only when their
 *   `seq` is newer than the last one applied for that key (seq 0 = unsequenced, always applied);
 * - fills are appended once per `fill_id`;
 * - a `Snapshot` (ring expired) replaces orders, positions, accounts, risk and session state;
 * - a `Welcome` for a different gateway session drops everything from the old one;
 * - a `Welcome` from a different gateway process (`boot_id`, CB6: the session id is stable per
 *   user across restarts) drops the old process's orders, positions, accounts, fills, risk and
 *   session state; the client then resumes from 0 (the new ring's start, or a Snapshot). The
 *   unconfirmed-position flags stay (the safe side) until the new process confirms;
 * - a `SessionState` for a different Rithmic login (`login_id`, ADR-0027) drops the old login's
 *   accounts, orders, positions, fills and risk: they belong to another Rithmic user;
 * - the gateway's `POSITIONS_UNCONFIRMED` / `POSITIONS_CONFIRMED` notices set / clear the
 *   account's unconfirmed flag (ADR-0032), keyed by seq like the rest. A `Snapshot` taken while
 *   the order plant is READY (`positions_checked`) carries the complete unconfirmed list and
 *   REPLACES the flags (audit AB-1: a client that starts from a snapshot never saw the notices;
 *   a flag the gateway has since cleared goes too). Any other snapshot keeps them (a stale
 *   warning is the safe side until the gateway says otherwise).
 * - the gateway's Error `FLATTEN_WAITING` / `FLATTEN_WAITING_CLEARED` notices set / clear the
 *   account's held-exit banner (ADR-0050: an exit held back because a protective order's cancel
 *   is unconfirmed past the wait), keyed by seq. A READY `Snapshot` (`positions_checked`)
 *   carries the held exits of its accounts and REPLACES those accounts' flags; other accounts'
 *   flags (a copy follower on another login) stay. A restarted gateway holds no exit (they are
 *   not persisted), so a new `boot_id` drops them.
 *
 * Generated protobuf code is only imported as types, so this runs under `node --test`.
 */
import type { Envelope } from "../gen/helm/v1/envelope_pb";
import type { CopyLogEntry, CopyState, RiskState } from "../gen/helm/v1/risk_pb";
import type { LoginStatus, SessionState } from "../gen/helm/v1/session_pb";
import type { AccountUpdate, FillEvent, OrderEvent, PositionUpdate } from "../gen/helm/v1/trading_pb";

/** Most recent fills kept in memory (newest first). */
export const MAX_FILLS = 1000;
/** Most recent copy-log lines kept in memory (newest first, ADR-0037). */
export const MAX_COPY_LOG = 200;

export type TradingState = {
  /** Gateway session the state belongs to (`Welcome.session_id`). */
  sessionId: string | null;
  /** Gateway process the state came from (`Welcome.boot_id`; "" from gateways that predate it). */
  bootId: string | null;
  /** Latest order-plant session state (SESSION_STATUS_*). */
  session: SessionState | null;
  /** Latest state of the session's own Rithmic login connection (ADR-0027). */
  login: LoginStatus | null;
  accounts: ReadonlyMap<string, AccountUpdate>;
  /** By order id (`OrderEvent.client_order_id`, the gateway's user_tag). */
  orders: ReadonlyMap<string, OrderEvent>;
  /** By `positionKey(account, symbol)`; flat positions stay (they carry realized P&L). */
  positions: ReadonlyMap<string, PositionUpdate>;
  /** Newest first, at most `MAX_FILLS`. */
  fills: readonly FillEvent[];
  risk: ReadonlyMap<string, RiskState>;
  /** Copy trader status (ADR-0037): the latest `CopyState`. */
  copy: CopyState | null;
  /** Copy log, newest first, at most `MAX_COPY_LOG` (the page loads older lines from the API). */
  copyLog: readonly CopyLogEntry[];
  /** Seq of the frame last applied per key. */
  seqs: ReadonlyMap<string, bigint>;
  fillIds: ReadonlySet<string>;
  /**
   * Accounts whose positions Rithmic has not confirmed (the gateway's `POSITIONS_UNCONFIRMED`
   * notice, until `POSITIONS_CONFIRMED`), with the gateway's text. HELM may show such an account
   * FLAT while a real position is open.
   */
  unconfirmedAccounts: ReadonlyMap<string, string>;
  /**
   * Accounts with an exit the gateway holds back past its wait because a protective order's
   * cancel is unconfirmed (Error `FLATTEN_WAITING`, ADR-0050), with the gateway's text: the
   * banner with "Force exit anyway".
   */
  heldExits: ReadonlyMap<string, string>;
};

export const EMPTY_TRADING: TradingState = {
  sessionId: null,
  bootId: null,
  session: null,
  login: null,
  accounts: new Map(),
  orders: new Map(),
  positions: new Map(),
  fills: [],
  risk: new Map(),
  copy: null,
  copyLog: [],
  seqs: new Map(),
  fillIds: new Set(),
  unconfirmedAccounts: new Map(),
  heldExits: new Map(),
};

/** Gateway notice: an account's positions are not confirmed by Rithmic (text starts "{account}: "). */
export const NOTICE_POSITIONS_UNCONFIRMED = "POSITIONS_UNCONFIRMED";
/** Gateway notice: an account's positions are confirmed by Rithmic again (text starts "{account}: "). */
export const NOTICE_POSITIONS_CONFIRMED = "POSITIONS_CONFIRMED";

/** Gateway notice (Error level): an account's exit is held past its wait (ADR-0050). */
export const NOTICE_FLATTEN_WAITING = "FLATTEN_WAITING";
/** Gateway notice: the account has no held exit any more (ADR-0050). */
export const NOTICE_FLATTEN_WAITING_CLEARED = "FLATTEN_WAITING_CLEARED";
/** Proto `NoticeLevel.ERROR`. */
const LEVEL_ERROR = 3;

/** Is this notice the Error `FLATTEN_WAITING` of a held exit (the banner, not a toast)? */
export function isHeldExitNotice(n: { level: number; code: string }): boolean {
  return n.code === NOTICE_FLATTEN_WAITING && n.level >= LEVEL_ERROR;
}

/**
 * The account a per-account gateway notice is about: `Notice.account_id`, or (gateways that
 * predate the field) the "{account}: " its text starts with.
 */
export function noticeAccount(n: { accountId?: string; text: string }): string | null {
  if (n.accountId) return n.accountId;
  const i = n.text.indexOf(": ");
  return i > 0 ? n.text.slice(0, i) : null;
}

/** The banner text for an account a `Snapshot` lists as unconfirmed (with the gateway's reason). */
export function snapshotUnconfirmedText(accountId: string, reason: string): string {
  return `${accountId}: positions not confirmed by Rithmic (${reason})`;
}

/**
 * Accounts whose positions are not confirmed: flagged by the gateway's notice, or carrying a
 * `PositionUpdate.mismatch` (sorted).
 */
export function unconfirmedAccountIds(s: Pick<TradingState, "positions" | "unconfirmedAccounts">): string[] {
  const ids = new Set(s.unconfirmedAccounts.keys());
  for (const p of s.positions.values()) if (p.mismatch) ids.add(p.accountId);
  return [...ids].sort();
}

/** The persistent banner's headline for unconfirmed accounts (empty when none). */
export function unconfirmedPositionsMessage(ids: readonly string[]): string {
  if (ids.length === 0) return "";
  const which = ids.length === 1 ? `account ${ids[0] ?? ""}` : `accounts ${ids.join(", ")}`;
  return `Positions for ${which} are UNCONFIRMED by Rithmic — TradePrizm may show FLAT while a position is open. Check and close positions in R|Trader. New orders, Flatten, Reverse and risk liquidation are disabled there.`;
}

export function positionKey(accountId: string, symbol: string): string {
  return `${accountId}\u001f${symbol}`;
}

/** Order states that are still live at the exchange (or may be). Mirrors proto `OrderState`. */
export const ORDER_STATE = {
  DRAFT: 1,
  PENDING_SUBMIT: 2,
  WORKING: 3,
  PARTIALLY_FILLED: 4,
  FILLED: 5,
  PENDING_CANCEL: 6,
  CANCELLED: 7,
  PENDING_MODIFY: 8,
  REJECTED: 9,
  TRIGGERED: 10,
  UNKNOWN: 11,
  NOT_PLACED: 12,
} as const;

const WORKING_STATES: ReadonlySet<number> = new Set([
  ORDER_STATE.PENDING_SUBMIT,
  ORDER_STATE.WORKING,
  ORDER_STATE.PARTIALLY_FILLED,
  ORDER_STATE.PENDING_CANCEL,
  ORDER_STATE.PENDING_MODIFY,
  ORDER_STATE.TRIGGERED,
  ORDER_STATE.UNKNOWN,
]);

/** Working (or possibly working) at the exchange: counts toward "working orders". */
export function isWorkingOrder(o: Pick<OrderEvent, "state">): boolean {
  return WORKING_STATES.has(o.state);
}

/** The highest seq applied for any key (0 when none). */
function newestSeq(seqs: ReadonlyMap<string, bigint>): bigint {
  let m = 0n;
  for (const v of seqs.values()) if (v > m) m = v;
  return m;
}

/** Apply one sequenced keyed frame; returns null when it is not newer than what we have. */
function keyed<V>(
  s: TradingState,
  seq: bigint,
  seqKey: string,
  map: ReadonlyMap<string, V>,
  key: string,
  value: V,
): { map: Map<string, V>; seqs: ReadonlyMap<string, bigint> } | null {
  let seqs = s.seqs;
  if (seq > 0n) {
    const prev = s.seqs.get(seqKey);
    if (prev !== undefined && seq <= prev) return null;
    const next = new Map(s.seqs);
    next.set(seqKey, seq);
    seqs = next;
  }
  const m = new Map(map);
  m.set(key, value);
  return { map: m, seqs };
}

function byKey<V>(items: readonly V[], key: (v: V) => string): Map<string, V> {
  const m = new Map<string, V>();
  for (const v of items) m.set(key(v), v);
  return m;
}

/** Pure reducer. Returns `s` itself when the frame changes nothing. */
export function reduceTrading(s: TradingState, env: Envelope): TradingState {
  const { seq, body } = env;
  switch (body.case) {
    case "welcome": {
      const id = body.value.sessionId;
      const bootId = body.value.bootId;
      if (s.sessionId === id) {
        // Same session id (it is the auth session, stable across gateway restarts). A new boot
        // id means a restarted gateway: its seqs start at 1 again and can overtake ours, so the
        // old process's per-key seqs and stale orders/positions must go (CB6). Without a boot id
        // (older gateway) a newest seq below one we applied is the only restart hint.
        const restarted =
          (s.bootId !== null && s.bootId !== bootId) || body.value.lastSeq < newestSeq(s.seqs);
        if (!restarted) return s.bootId === bootId ? s : { ...s, bootId };
        return { ...EMPTY_TRADING, sessionId: id, bootId, unconfirmedAccounts: s.unconfirmedAccounts };
      }
      // A different gateway session: nothing from the old one is authoritative any more.
      return { ...EMPTY_TRADING, sessionId: id, bootId };
    }
    case "snapshot": {
      const snap = body.value;
      return {
        ...s,
        session: snap.sessions.at(-1) ?? s.session,
        accounts: byKey(snap.accounts, (a) => a.accountId),
        orders: byKey(snap.orders, (o) => o.clientOrderId),
        positions: byKey(snap.positions, (p) => positionKey(p.accountId, p.symbol)),
        risk: byKey(snap.risk, (r) => r.accountId),
        copy: snap.copy.at(-1) ?? s.copy,
        unconfirmedAccounts: snap.positionsChecked
          ? new Map(
              snap.unconfirmed.map((u) => [
                u.accountId,
                // Keep the notice's fuller text when this client already has one.
                s.unconfirmedAccounts.get(u.accountId) ?? snapshotUnconfirmedText(u.accountId, u.reason),
              ]),
            )
          : s.unconfirmedAccounts,
        heldExits: snap.positionsChecked ? snapshotHeldExits(s.heldExits, snap) : s.heldExits,
        seqs: new Map(),
      };
    }
    case "sessionState": {
      let base = s;
      if (seq > 0n) {
        const prev = s.seqs.get("session");
        if (prev !== undefined && seq <= prev) return s;
      }
      if (s.session !== null && s.session.loginId !== body.value.loginId) {
        // Another Rithmic login: nothing of the old one's trading state applies.
        base = {
          ...EMPTY_TRADING,
          sessionId: s.sessionId,
          login: s.login,
          copy: s.copy,
          copyLog: s.copyLog,
          seqs: s.seqs,
        };
      }
      if (seq > 0n) {
        const seqs = new Map(base.seqs);
        seqs.set("session", seq);
        return { ...base, session: body.value, seqs };
      }
      return { ...base, session: body.value };
    }
    case "loginStatus": {
      if (seq > 0n) {
        const prev = s.seqs.get("login");
        if (prev !== undefined && seq <= prev) return s;
        const seqs = new Map(s.seqs);
        seqs.set("login", seq);
        return { ...s, login: body.value, seqs };
      }
      return { ...s, login: body.value };
    }
    case "account": {
      const a = body.value;
      const r = keyed(s, seq, `a:${a.accountId}`, s.accounts, a.accountId, a);
      return r ? { ...s, accounts: r.map, seqs: r.seqs } : s;
    }
    case "orderEvent": {
      const o = body.value;
      const r = keyed(s, seq, `o:${o.clientOrderId}`, s.orders, o.clientOrderId, o);
      return r ? { ...s, orders: r.map, seqs: r.seqs } : s;
    }
    case "position": {
      const p = body.value;
      const k = positionKey(p.accountId, p.symbol);
      const r = keyed(s, seq, `p:${k}`, s.positions, k, p);
      return r ? { ...s, positions: r.map, seqs: r.seqs } : s;
    }
    case "riskState": {
      const rs = body.value;
      const r = keyed(s, seq, `r:${rs.accountId}`, s.risk, rs.accountId, rs);
      return r ? { ...s, risk: r.map, seqs: r.seqs } : s;
    }
    case "copyState": {
      if (seq > 0n) {
        const prev = s.seqs.get("copy");
        if (prev !== undefined && seq <= prev) return s;
        const seqs = new Map(s.seqs);
        seqs.set("copy", seq);
        return { ...s, copy: body.value, seqs };
      }
      return { ...s, copy: body.value };
    }
    case "copyLog": {
      // Replayed frames carry their seq: a line already applied (same seq) is not added twice.
      if (seq > 0n) {
        const prev = s.seqs.get("copylog");
        if (prev !== undefined && seq <= prev) return s;
        const seqs = new Map(s.seqs);
        seqs.set("copylog", seq);
        return { ...s, copyLog: [body.value, ...s.copyLog].slice(0, MAX_COPY_LOG), seqs };
      }
      return { ...s, copyLog: [body.value, ...s.copyLog].slice(0, MAX_COPY_LOG) };
    }
    case "notice": {
      const n = body.value;
      if (isHeldExitNotice(n) || n.code === NOTICE_FLATTEN_WAITING_CLEARED) return heldExitNotice(s, seq, n);
      const confirmed = n.code === NOTICE_POSITIONS_CONFIRMED;
      if (!confirmed && n.code !== NOTICE_POSITIONS_UNCONFIRMED) return s;
      const account = noticeAccount(n);
      if (account === null) return s;
      let seqs = s.seqs;
      if (seq > 0n) {
        const k = `u:${account}`;
        const prev = s.seqs.get(k);
        if (prev !== undefined && seq <= prev) return s;
        const next = new Map(s.seqs);
        next.set(k, seq);
        seqs = next;
      }
      const flags = new Map(s.unconfirmedAccounts);
      if (confirmed) flags.delete(account);
      else flags.set(account, n.text);
      return { ...s, unconfirmedAccounts: flags, seqs };
    }
    case "fill": {
      const f = body.value;
      if (s.fillIds.has(f.fillId)) return s;
      const fills = [f, ...s.fills];
      const ids = new Set(s.fillIds);
      ids.add(f.fillId);
      if (fills.length > MAX_FILLS) {
        for (const dropped of fills.splice(MAX_FILLS)) ids.delete(dropped.fillId);
      }
      return { ...s, fills, fillIds: ids };
    }
    default:
      return s;
  }
}

/** A READY snapshot's held exits replace its accounts' flags; other accounts' flags stay. */
function snapshotHeldExits(
  prev: ReadonlyMap<string, string>,
  snap: {
    accounts: readonly { accountId: string }[];
    heldExits: readonly { accountId: string; text: string }[];
  },
): Map<string, string> {
  const covered = new Set(snap.accounts.map((a) => a.accountId));
  const m = new Map([...prev].filter(([a]) => !covered.has(a)));
  for (const h of snap.heldExits) m.set(h.accountId, h.text);
  return m;
}

function heldExitNotice(
  s: TradingState,
  seq: bigint,
  n: { level: number; code: string; text: string; accountId?: string },
): TradingState {
  const account = noticeAccount(n);
  if (account === null) return s;
  let seqs = s.seqs;
  if (seq > 0n) {
    const k = `h:${account}`;
    const prev = s.seqs.get(k);
    if (prev !== undefined && seq <= prev) return s;
    const next = new Map(s.seqs);
    next.set(k, seq);
    seqs = next;
  }
  const held = new Map(s.heldExits);
  if (n.code === NOTICE_FLATTEN_WAITING_CLEARED) held.delete(account);
  else held.set(account, n.text);
  return { ...s, heldExits: held, seqs };
}

/**
 * Account ids known to this session: `AccountUpdate`s first (login order), then any id seen only
 * on a position or order.
 */
export function accountIds(s: TradingState): string[] {
  const ids = new Set(s.accounts.keys());
  for (const p of s.positions.values()) ids.add(p.accountId);
  for (const o of s.orders.values()) ids.add(o.accountId);
  return [...ids];
}
